import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Search, Users2 } from "lucide-react";

/**
 * Admin-only panel for the Influencer Library.
 * - Upload PDF / XLSX / CSV → parsed + upserted on backend.
 * - Browse with search/filter.
 * - Soft-delete entries (is_active=false).
 *
 * Rendered as a new tab inside the existing /admin page.
 */
export default function InfluencerLibraryPanel() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [q, setQ] = useState("");
    const [city, setCity] = useState("");
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [defaultCity, setDefaultCity] = useState("");
    const fileRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q) params.set("q", q);
            if (city) params.set("city", city);
            params.set("limit", "500");
            const { data } = await api.get(`/admin/influencers?${params.toString()}`);
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch {
            toast.error("Failed to load influencer library");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            if (defaultCity) fd.append("default_city", defaultCity);
            const { data } = await api.post("/admin/influencers/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 120000,
            });
            toast.success(`Parsed ${data.parsed} · ${data.inserted} new · ${data.updated} updated`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.userMessage || "Upload failed");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const del = async (id) => {
        if (!window.confirm("Remove this influencer from the library?")) return;
        try {
            await api.delete(`/admin/influencers/${id}`);
            toast.success("Removed");
            await load();
        } catch { toast.error("Delete failed"); }
    };

    return (
        <div className="space-y-6" data-testid="admin-influencer-panel">
            {/* Header */}
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="label-micro flex items-center gap-2 mb-1">
                        <Users2 className="w-3.5 h-3.5" /> Influencer Library
                    </div>
                    <h2 className="font-display text-2xl tracking-tight">Directory for on-demand matching</h2>
                    <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
                        Upload a PDF / Excel / CSV of influencers. Parsed entries are deduped by Instagram
                        handle. The CEO matches this library on-demand per campaign — never automatically.
                    </p>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 border border-zinc-800 px-2 py-1 rounded-sm">
                    {total} in library
                </div>
            </div>

            {/* Upload */}
            <div className="card-base p-5">
                <div className="label-micro mb-3">Upload</div>
                <div className="flex flex-wrap items-center gap-3">
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".pdf,.xlsx,.csv,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                        onChange={(e) => upload(e.target.files?.[0])}
                        className="hidden"
                        data-testid="influencer-upload-input"
                    />
                    <button
                        data-testid="influencer-upload-btn"
                        disabled={uploading}
                        onClick={() => fileRef.current?.click()}
                        className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50">
                        <Upload className="w-4 h-4" /> {uploading ? "Parsing…" : "Upload PDF / Excel / CSV"}
                    </button>
                    <input
                        data-testid="influencer-default-city"
                        placeholder="Default city (optional, e.g. Surat)"
                        value={defaultCity}
                        onChange={(e) => setDefaultCity(e.target.value)}
                        className="form-input text-sm w-72"
                    />
                </div>
                <div className="text-[10px] text-zinc-500 mt-3">
                    Supported columns (Excel/CSV): <span className="font-mono">name, handle, category, city, platform, followers, tags, notes</span>.
                    PDFs are parsed heuristically — Instagram URLs are the strongest signal.
                </div>
            </div>

            {/* Search + list */}
            <div className="card-base p-5">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                        <input
                            data-testid="influencer-search"
                            placeholder="Search name or handle"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && load()}
                            className="form-input text-sm pl-8 w-full"
                        />
                    </div>
                    <input
                        data-testid="influencer-city-filter"
                        placeholder="City"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && load()}
                        className="form-input text-sm w-40"
                    />
                    <button onClick={load} className="btn-secondary text-sm" data-testid="influencer-filter-apply">Apply</button>
                </div>

                {loading ? (
                    <div className="text-sm text-zinc-500 py-6 text-center">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="text-sm text-zinc-500 py-6 text-center">
                        No influencers yet. Upload a file above to get started.
                    </div>
                ) : (
                    <div className="overflow-x-auto -mx-5 px-5">
                        <table className="w-full text-xs">
                            <thead className="text-zinc-500">
                                <tr className="border-b border-zinc-900">
                                    <th className="text-left font-medium uppercase tracking-wider py-2 pr-3">Name</th>
                                    <th className="text-left font-medium uppercase tracking-wider py-2 pr-3">Handle</th>
                                    <th className="text-left font-medium uppercase tracking-wider py-2 pr-3">City</th>
                                    <th className="text-left font-medium uppercase tracking-wider py-2 pr-3">Category</th>
                                    <th className="text-left font-medium uppercase tracking-wider py-2 pr-3">Source</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((x) => (
                                    <tr key={x.id} className="border-b border-zinc-900/50 hover:bg-zinc-900/30">
                                        <td className="py-2 pr-3 text-zinc-100">{x.name}</td>
                                        <td className="py-2 pr-3 font-mono text-orange-400">
                                            {x.handle ? (
                                                <a target="_blank" rel="noopener noreferrer"
                                                    href={`https://instagram.com/${x.handle}`}
                                                    className="hover:underline">@{x.handle}</a>
                                            ) : <span className="text-zinc-600">—</span>}
                                        </td>
                                        <td className="py-2 pr-3 text-zinc-300">{x.city || "—"}</td>
                                        <td className="py-2 pr-3 text-zinc-300">{x.category || "—"}</td>
                                        <td className="py-2 pr-3 text-zinc-500 text-[10px] uppercase tracking-wider">{x.source}</td>
                                        <td className="py-2 pr-3 text-right">
                                            <button
                                                data-testid={`influencer-del-${x.id}`}
                                                onClick={() => del(x.id)}
                                                className="p-1 text-zinc-500 hover:text-red-400" title="Remove">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
